import { formatMessage, randomMessage } from "model/message";
import Debug from "debug";
import { standingChanged } from "lib/strings/city";
import clock from "util/clock";
const debug = Debug("actions:rob");
const action = "Rob";
const skill = "crime";
const ROB_HEALTH_MIN = 15;

const STATUS_METRICS = {
  intimidation: 0.5,
  influence: -0.2,
  respect: -0.5,
};
/**
 * The three town verbs are meant to be three different bets, and the numbers here are what make
 * rob the third one.
 *
 * Beg is slow and safe: no health, a few coins and a little food, and it pays every time the
 * town is willing. Work is the reliable middle: it always succeeds, it always costs health,
 * and it hands over a day's food and a day's wage. Rob is the gamble — half of them fail
 * outright at the start, a third of the failures leave a mark, and it gives no food at all.
 * What it has that neither of the others does is the top end: one robbery in five that lands
 * is a score, several times the haul, and at any level that is more money than a day of work
 * has ever paid.
 *
 * It is also the only one of the three that runs at night (`enabled` gates on isNight), so it
 * is not competing with work for the same hours — the question it asks is whether to spend the
 * dark on it or sleep through it.
 */

const MAX_ROB_SUCCESS = 0.9;
const MAX_ROB_HEALTH = 10;
// The ceiling on the haul. An amateur can still lift a purse, so the level term is added to a
// floor rather than multiplying one — `BASE_ROB_COINS * skillImprovement` alone was zero at
// level 0, which paid exactly one coin a night for the whole of the first town.
const MIN_ROB_COINS = 12;
const BASE_ROB_COINS = 20;
// The tail, and the whole reason to take the risk. A fifth of successful robberies pay several
// times over — at level 0 that is up to 72 coins against a day of work's 1 to 10.
const SCORE_CHANCE = 0.2;
const SCORE_MIN = 3;
const SCORE_MAX = 6;

// Getting hurt is its own roll, separate from whether the robbery paid. A night can go wrong
// on the way out of a house that gave up its money, and a man can come back empty and whole.
// Failure is the likelier of the two to leave a mark; success carries a quarter of that.
const ROB_INJURY_CHANCE = 0.5;
const ROB_INJURY_ON_SUCCESS = 0.25;
// Skill buys its way out of trouble but never the whole way. An Outlaw Legend is still a man
// walking into a dark house.
const MIN_INJURY_CHANCE = 0.05;
// What skill takes off the size of a wound, at most: the eleven crime levels run the cap from
// MAX_ROB_HEALTH down to a fifth of it.
const INJURY_SKILL_RELIEF = 0.8;

const ROB_CHARISMA = 5;
const ROB_INFAMY = 10;

/**
 * None of these lines may name the method, and that is the whole constraint.
 *
 * The button is not always `Rob` — the crime levels rename it Ruse, Resell, Rustle, Rough Up,
 * Raid (skills/crime/index.js). One set of lines has to survive all six, so they report the
 * transaction and the leaving: a man is short what he had, nobody comes after you, you walk. A
 * line about slipping a hand into a coat is wrong the moment the button says Rustle.
 *
 * A line may only put a mark on him when the roll actually did. `attempt` rolls the injury
 * separately from the takings, so all four corners exist — paid and whole, paid and cut,
 * caught and whole, caught and cut — and each has its own list. A wound named on a night that
 * cost nothing is the same lie as a **health -6** with no wound in the sentence.
 *
 * Rob is the night half of the town's loop (`enabled` gates on calendar.isNight), so the dark
 * is a given and does not need saying every time. Town ambient is already reporting the hour.
 */
const took = [
  "it is done before he knows it is being done.",
  "he does not call out. he does not come after you.",
  "take it and go by the back way and do not run.",
  "the weight of it in the pocket and the street empty behind.",
  "whatever he was saving it for, he is not saving it now.",
  "a hand where it should not be, and then gone.",
];

const tookAndPaid = [
  "paid for it on the way out.",
  "he does not give it up easy. take it anyway.",
  "something opens along the ribs. keep walking.",
];

const missed = [
  "the shadows yield nothing.",
  "hands come out of it empty.",
  "someone is up and moving in there. back the way you came.",
];

const missedAndPaid = [
  "a boot out of the dark and then the ground.",
  "they get hold of you first. nothing to show for it.",
  "back the way you came, bleeding and no richer.",
];

// The score. Rare enough that it is worth a set of its own, and none of them says how much —
// the number is already coming, and it is the size of the number that does the work.
const struck = [
  "it does not all fit in the one pocket.",
  "somebody's whole year, and it weighs nothing.",
  "enough to stop in the road and count it twice.",
  "he was keeping it for something. now you are.",
];

const message = ({ success, score, cost, gain }) => {
  const hurt = cost.health > 0;
  if (!success) {
    return formatMessage(randomMessage(hurt ? missedAndPaid : missed), {
      cost,
      gain,
    });
  }
  // Hurt outranks the score: the body is louder than the money, and a score that cost blood
  // reads better as `paid for it on the way out.` against **coins +58** than as a boast with a
  // wound stapled to it. The takings are in the numbers either way.
  const baseMessage = hurt
    ? randomMessage(tookAndPaid)
    : randomMessage(score ? struck : took);
  return formatMessage(`${baseMessage}`, { cost, gain });
};

// Infamy and charisma are both in `gain` — `cost` only ever carries health. Read off `cost`,
// these two guards were `undefined` and `undefined < 0`, so neither line had ever printed and
// the whole notification was the standing crossing or nothing.
const notification = ({ gain }, totalInfamy, totalCharisma, newStatus) => {
  const infamy = randomMessage([
    "whispers follow you now.",
    "your name is said in places you have not been.",
  ]);
  const charisma = randomMessage([
    "eyes turn colder.",
    "people cross to the far side of the road.",
  ]);

  const messages = [];
  if (gain.infamy && totalInfamy % 10 === 0) messages.push(infamy);
  // The `> 0` is what keeps this off the screen at the start of a run. Traits.modifyTrait floors
  // at zero, so a man with no charisma to lose does not lose any — but `0 % 10 === 0` is true,
  // and without this a quarter of all robberies reported a fall that never happened. Infamy
  // above needs no such guard: it only ever goes up, so its total is at least 1 when it fires.
  if (gain.charisma < 0 && totalCharisma > 0 && totalCharisma % 10 === 0) {
    messages.push(charisma);
  }
  // Worded once in lib/strings/city.js — the crossing is the town's, not this action's, and
  // beg and work reach the same three lines.
  if (newStatus) {
    const line = standingChanged(newStatus);
    if (line) messages.push(line);
  }
  return messages.length > 0 ? messages.join(" ") : null;
};

const getAdjustmentFactor = (party) => {
  const levelIndex = party.progression.getCurrentLevelIndex(skill);
  // Assuming levelIndex starts at 0 for the first level and increases from there
  const experienceLevel = Math.max(0, levelIndex); // Ensures no negative values

  // Calculate improvement factors based on experienceLevel
  const skillImprovement = Math.pow(experienceLevel * 0.1, 2); // Example: Quadratic growth for skill impact
  const successFactor = Math.min(MAX_ROB_SUCCESS, 0.5 + skillImprovement); // Ensures success rate does not exceed MAX_ROB_SUCCESS
  // How often it goes wrong, and how badly. Both fall off with skill, and neither reaches zero.
  // These used to be one number, `ROB_HEALTH_MIN - skillImprovement`, subtracted from
  // MAX_ROB_HEALTH — 15 against a term that never exceeds 1, so the cost clamped to 0 at every
  // level and a robbery had never once drawn blood.
  const injuryChance = Math.max(
    MIN_INJURY_CHANCE,
    ROB_INJURY_CHANCE * (1 - skillImprovement),
  );
  const injuryMax = Math.max(
    1,
    Math.round(MAX_ROB_HEALTH * (1 - skillImprovement * INJURY_SKILL_RELIEF)),
  );
  const haulCeiling = MIN_ROB_COINS + BASE_ROB_COINS * skillImprovement; // 12 at the start, 32 at the end

  debug(
    `Level: ${experienceLevel}, Success Factor: ${successFactor}, Injury: ${injuryChance} up to ${injuryMax}, Haul Ceiling: ${haulCeiling}`,
  );
  return [successFactor, injuryChance, injuryMax, haulCeiling];
};

const attempt = ({ party }) => {
  // Adjust success rate based on infamy and charisma
  const [successFactor, injuryChance, injuryMax, haulCeiling] =
    getAdjustmentFactor(party);
  const success = Math.random() < successFactor;
  // A separate roll from the takings: a house can give up its money and still put a boot in
  // you on the way out. Being caught is the likelier of the two to leave a mark.
  const hurt = Math.random() <
    (success ? injuryChance * ROB_INJURY_ON_SUCCESS : injuryChance);
  // Never more than MAX_ROB_HEALTH, and the button needs ROB_HEALTH_MIN to press, so a
  // robbery can put a man on the floor but cannot be the thing that kills him.
  const healthCost = hurt ? Math.ceil(Math.random() * injuryMax) : 0;

  // The ordinary night, and then the one that is not. A score multiplies the haul rather than
  // replacing it, so skill lifts both ends of the range at once.
  const score = success && Math.random() < SCORE_CHANCE;
  const haul = Math.ceil(Math.random() * haulCeiling);
  const coinGain = !success ? 0 : score
    ? Math.round(haul * (SCORE_MIN + Math.random() * (SCORE_MAX - SCORE_MIN)))
    : haul;

  const result = {
    success,
    score,
    cost: {
      health: healthCost,
    },
    gain: {
      coins: coinGain,
      charisma: success ? 0 : Math.random() < 0.5 ? -1 : 0, // Losing charisma on unsuccessful attempts
      infamy: success ? (Math.random() < 0.5 ? 1 : 0) : 0, // Gaining infamy on success
    },
  };

  if (!result.success) return result;

  // Adjust for total robberies impacting charisma and infamy
  const totalRobberies = party.history.total(action) + 1;
  if (totalRobberies % ROB_CHARISMA === 0) {
    result.gain.charisma -= 1; // Diminish charisma further based on number of robberies
  }
  if (totalRobberies % ROB_INFAMY === 0) {
    result.gain.infamy += 1; // Increase infamy based on number of robberies
  }
  return result;
};

const executeSideEffects = (context, result) => {
  const { party, calendar, political } = context;
  const { gain, cost } = result;
  const { history, resources, traits, progression, city } = party;

  // Track action
  history.add({ action, result }, context);

  // The law picks up the trail, and keeps it until the party crosses out of this region. Set
  // on the attempt rather than on `result.success`: a robbery that went wrong is the one where
  // somebody got a good look at you. Whether anyone actually comes is infamy's business —
  // skills/crime/events/randomEvents rolls it, here on the night and again on every step of
  // the road out.
  party.markPursued();

  // Update resources
  resources.delta(
    {
      coins: gain.coins,
      health: -cost.health,
    },
    context,
  );
  traits.delta(
    {
      infamy: gain.infamy,
      charisma: gain.charisma,
    },
    context,
  );
  // Update progression
  progression.addExperience(skill, 1, traits, context);

  const { status, isNewStatus } = political.updateStatusMetrics(
    city,
    result.success ? STATUS_METRICS : {},
  );

  // Time passes
  // The player bought this phase. Rebaseline the clock so the accumulator, which may
  // already be nearly full, does not hand over a second phase moments later.
  calendar.nextStep(context);
  clock.reset();

  // notify of the results
  return {
    message: message(result),
    notification: notification(
      result,
      traits.getTraitValue("infamy"),
      traits.getTraitValue("charisma"),
      isNewStatus ? status : null,
    ),
  };
};

const rob = {
  title: ({ party: { progression } }) =>
    progression.getCurrentLevel(skill).actionLabel || "Rob",
  hotkey: "r",
  type: action,
  level: ({ party }) => party.progression.getCurrentLevelIndex(skill),
  enabled: ({ calendar, party }) => {
    return party.resources.attribs.health.value >= ROB_HEALTH_MIN &&
      calendar.isNight();
  },
  execute: (context) => {
    const result = attempt(context);
    const { event, message, notification } = executeSideEffects(
      context,
      result,
    );

    return { result: { message }, outcome: { event }, notification };
  },
  tooltips: [["health", `0 - ${MAX_ROB_HEALTH}`]],
};

export { action as RobType, rob as default };
